import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, AutomationInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Observable } from 'rxjs';
import { getSingularEntityTypeDisplayTranslationString } from '../../../../shared/entities';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../common/automation-display.service';
import { periodDays, rateLimitingTypeSpecificEntity } from './rate-filter-panel/rate-filter-panel.component';

@Injectable({ providedIn: 'root' })
export class RateFilterService extends AutomationDisplayService {
  constructor(protected readonly translateService: TranslateService) {
    super();
  }

  /** Gets text for the activity column in the table. */
  getActivityColumn(_namespace: string, activity: ActivityInterface): ActivityTableEntry {
    if (activity?.error) {
      return this.tableEntry(
        this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTIVITY.FAILURE'),
        this.emptyBody(),
      );
    }

    const nextStepId = activity?.nextWorkflowStep?.id;
    const executedStep = activity?.workflowStep;
    let displayContent = 'AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTIVITY.YES_BRANCH';

    if (executedStep.choices?.defaultNextStep === nextStepId) {
      displayContent = 'AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTIVITY.NO_BRANCH';
    }

    // Show user-friendly text if the step proceeded.
    return this.tableEntry(
      this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTION.TITLE'),
      this.translateService.stream(displayContent),
    );
  }

  /** The text in an automation node (i.e. the nodes in the graph). */
  getAutomationNodeData(
    _namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
    automation: AutomationInterface,
  ): Observable<NodeData> {
    const data = step?.data ? JSON.parse(step.data) : {};
    return this.nodeDisplayData(
      this.translateService.stream(this.getSubtitle(data, automation)),
      this.noValidationErrorsDetected(),
    );
  }

  private getSubtitle(data: any, automation: AutomationInterface): string {
    const entityType = this.translateService.instant(
      getSingularEntityTypeDisplayTranslationString(automation.entityType),
    );
    return this.calculateSubtitleText(data, entityType);
  }

  calculateSubtitleText(data: any, entityType: string): string {
    let accountString = this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ANY_ENTITY', {
      entity: entityType.toLowerCase(),
    });
    if (data.rate_limiting_type === rateLimitingTypeSpecificEntity) {
      accountString = this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.SPECIFIC_ENTITY', {
        entity: entityType.toLowerCase(),
      });
    }
    if (entityType !== 'Account') {
      accountString += ` ${this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ON_ACCOUNT')}`;
    }
    const length = +data.length > 1 ? data.length : '';
    let period =
      +data.length > 1
        ? this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTIVITY.HOURS')
        : this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTIVITY.HOUR');
    if (data.period === periodDays) {
      period =
        +data.length > 1
          ? this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTIVITY.DAYS')
          : this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTIVITY.DAY');
    }
    const time =
      +data.frequency > 1
        ? this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTIVITY.TIMES')
        : this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTIVITY.TIME');
    return this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTIVITY.TITLE_MORE', {
      rateLimitingType: accountString,
      frequency: data.frequency,
      length: length,
      period: period,
      time: time,
    });
  }
}
